import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBase64 } from 'Helpers/getbase64';
import useSession from 'Hooks/session.hook';
import errorHandler from 'Utils/error-handler';
import { omitBy, isUndefined, isEmpty, isNull } from 'lodash';
import { Divider, Row, Col, Space } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import CountrySelector from 'Components/shared/country-selector/country-selector';
import {
    AlertBox,
    CheckboxGroup,
    DatePicker,
    ImageUpload,
    PhoneInput,
    RadioGroup,
    TextInput,
    Button,
    SelectInput,
    Password
} from '@life-line-lab/components-react';
import moment from 'moment';
import { AccountCreateSchema } from './account-create-validation';

interface Input {
    fullName: string;
    identificationType?: string;
    identificationNumber?: string;
    identificationIssueDate: moment.Moment | null;
    identificationExpiryDate: moment.Moment | null;
    identificationPlaceOfIssue?: string;
    contactNumber: { dialCode: string, number: string };
    email: string;
    gender?: string;
    moType?: string;
    password: string;
    confirmPwd: string;
}

interface AccountFormProps {
    data?: Input;
    status?: string;
    resError?: any;
    onSubmit: (input: any) => void;
    onCancel: (input: any) => void;
}

const AccountCreateForm: React.FC<AccountFormProps> = () => {

    const { t } = useTranslation();

    const elRef = React.useRef<HTMLHeadingElement>(null);
    const scrollToRef = (ref: any) => ref.current.scrollIntoView(false);

    const options = [
        { value: 'DOCTOR', label: t('option.doctor') },
        { value: 'NURSE', label: t('option.nurse') },
        { value: 'MEDICAL-ASSISTANT', label: t('option.medicalAssistant') },
    ]

    const initialInput = {
        identificationType: 'NRIC',
        identificationIssueDate: null,
        identificationExpiryDate: null,
        contactNumber: { dialCode: '60', number: '' },
    };

    const initialError = {};

    const [input, setInput] = useState<any>(initialInput);
    const [error, setError] = useState<any>(initialError);
    const [stateOption, setStateOption] = useState<any>([]);
    const [countryOption, setCountryOption] = useState<any>([]);
    const [citiesOption, setCitiesOption] = useState<any>([]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError(initialError);
        setInput({ ...input, [e.target.name]: e.target.value });
    }

    const handleRadioChange = (value: string, name: string,) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }

    const handleDateChange = (value: moment.Moment, name: string) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }

    const handleSelectChange = (value: string | string[], name: string) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }

    const handlePhoneChange = (value: { dialCode: string, number: string }, name: string) => {
        setError(initialError);
        setInput({ ...input, [name]: value });
    }

    const handleSubmit = () => {
        try {
            const valInput = {
                ...input,
                identificationIssueDate: input.identificationIssueDate ? moment(input.identificationIssueDate).toISOString() : null,
                identificationExpiryDate: input.identificationExpiryDate ? moment(input.identificationExpiryDate).toISOString() : null,
            }
            const value = AccountCreateSchema.validateSync(omitBy(valInput, isNull), {
                strict: true,
                abortEarly: true,
                stripUnknown: false
            });
        } catch (err) {
            console.log(err);
            const { path, value } = errorHandler.validation(err);
            setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
        }
    }

    return (
        <div>
            <form>
                <Space className="account-form--inner" direction="vertical" size="large">
                    <div ref={elRef}>
                        <AlertBox isFilled={true} text={error.general} />
                    </div>

                    <div>
                        {/* <CheckboxGroup
                            direction="vertical"
                            isRequired={true}
                            isError={!!error.moType}
                            label={t('label.moType')}
                            name="moType"
                            value={input.moType}
                            onChange={
                                (value: string) =>
                                    handleSelectChange(value, 'moType')
                            }
                            options={options}
                        /> */}
                        <RadioGroup
                            direction="vertical"
                            isRequired={true}
                            isError={!!error.moType}
                            label={t('label.moType')}
                            name="moType"
                            value={input.moType}
                            onChange={(event) => handleSelectChange(event.target.value, 'moType')}
                            options={[
                                { value: 'DOCTOR', label: t('option.doctor') },
                                { value: 'NURSE', label: t('option.nurse') },
                                { value: 'MEDICAL-ASSISTANT', label: t('option.medicalAssistant') },
                            ]}
                        />
                        <AlertBox text={error.moType} />
                    </div>

                    <div>
                        <TextInput
                            isRequired={true}
                            isError={!!error.fullName}
                            label={t('label.fullName')}
                            name="fullName"
                            value={input.fullName}
                            onChange={handleChange}
                        />
                        <AlertBox text={error.fullName} />
                    </div>

                    <div>
                        <RadioGroup
                            direction="vertical"
                            isRequired={true}
                            isError={!!error.gender}
                            label={t('label.gender')}
                            name="gender"
                            value={input.gender}
                            onChange={(event) => handleRadioChange(event.target.value, 'gender')}
                            options={[
                                { value: 'MALE', label: t('option.male') },
                                { value: 'FEMALE', label: t('option.female') },
                            ]}
                        />
                        <AlertBox text={error.gender} />
                    </div>

                    <div>
                        <RadioGroup
                            direction="vertical"
                            isRequired={true}
                            isError={!!error.identificationType}
                            label={t('label.identificationType')}
                            name="identificationType"
                            value={input.identificationType}
                            onChange={(e: any) => handleRadioChange(e.target.value, 'identificationType')}
                            options={[
                                { value: 'NRIC', label: t('option.nric').toUpperCase() },
                                { value: 'PASSPORT', label: t('option.passport') },
                            ]}
                        />
                        <AlertBox text={error.identificationType} />
                    </div>

                    <div>
                        <TextInput
                            isRequired={true}
                            isError={!!error.identificationNumber}
                            label={input.identificationType == 'NRIC'
                                ? t('label.identificationNumber')
                                : t('label.passportNumber')
                            }
                            name="identificationNumber"
                            value={input.identificationNumber}
                            onChange={handleChange}
                        />
                        <AlertBox text={error.identificationNumber} />
                    </div>

                    {input.identificationType == 'PASSPORT' &&
                        <React.Fragment>
                            <Row gutter={16}>
                                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24} >
                                    <div>
                                        <DatePicker
                                            isRequired={true}
                                            isError={!!error.identificationIssueDate}
                                            suffix={<CalendarOutlined />}
                                            label={t('label.identificationIssueDate')}
                                            placeholder="YYYY-MM-DD"
                                            value={input.identificationIssueDate ? moment(input.identificationIssueDate) : null}
                                            disabledDate={
                                                (currentDate: any) => {
                                                    return currentDate && currentDate.valueOf() >= Date.now();
                                                }
                                            }
                                            onChange={
                                                (value: any) =>
                                                    handleDateChange(value, 'identificationIssueDate')
                                            }
                                        />
                                        <AlertBox text={error.identificationIssueDate} />
                                    </div>
                                </Col>

                                <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24} >
                                    <div>
                                        <DatePicker
                                            isRequired={true}
                                            isError={!!error.identificationExpiryDate}
                                            suffix={<CalendarOutlined />}
                                            label={t('label.identificationExpiryDate')}
                                            placeholder="YYYY-MM-DD"
                                            value={input.identificationExpiryDate ? moment(input.identificationExpiryDate) : null}
                                            disabledDate={
                                                (currentDate: any) => {
                                                    return currentDate && currentDate.valueOf() < Date.now();
                                                }
                                            }
                                            onChange={
                                                (value: any) =>
                                                    handleDateChange(value, 'identificationExpiryDate')
                                            }
                                        />
                                        <AlertBox text={error.identificationExpiryDate} />
                                    </div>
                                </Col>
                            </Row>

                            <div>
                                <CountrySelector
                                    isRequired={true}
                                    isError={!!error.identificationPlaceOfIssue}
                                    label={t('label.identificationPlaceOfIssue')}
                                    name="identificationPlaceOfIssue"
                                    value={input.identificationPlaceOfIssue}
                                    onChange={(value: string) => handleSelectChange(value, 'identificationPlaceOfIssue')}
                                />
                                <AlertBox text={error.identificationPlaceOfIssue} />
                            </div>
                        </React.Fragment>
                    }

                    <div>
                        <PhoneInput
                            isRequired={false}
                            isError={!!error.contactNumber}
                            label={t('label.contactNumber')}
                            name="contactNumber"
                            value={input.contactNumber}
                            onChange={(value: any) => handlePhoneChange(value, 'contactNumber')}
                        />
                        <AlertBox text={error.contactNumber} />
                    </div>

                    <div>
                        <TextInput
                            isRequired={true}
                            isError={!!error.email}
                            label={t('label.email')}
                            name="email"
                            value={input.email}
                            onChange={handleChange}
                        />
                        <AlertBox text={error.email} />
                    </div>
                    {/* pwd Note: no longer need password
                    <div>
                        <Password
                            isRequired={true}
                            isError={!!error.password}
                            label={t('label.password')}
                            name="password"
                            value={input.password}
                            onChange={handleChange}
                        />
                        <AlertBox text={error.password} />
                    </div>
                    // confirm pwd 
                    <div>
                        <Password
                            isRequired={true}
                            isError={!!error.confirmPwd}
                            label={t('label.confirmPwd')}
                            name="confirmPwd"
                            value={input.confirmPwd}
                            onChange={handleChange}
                        />
                        <AlertBox text={error.confirmPwd} />
                    </div> */}

                    <div className="form--actions">
                        <Button
                            isLoading={status === "loading"}
                            type="primary"
                            label={t('action.submit')}
                            size="large"
                            onClick={handleSubmit}
                        />
                    </div>
                </Space>
            </form>
        </div>
    )
}

export default AccountCreateForm
